// Gap 2 (DA-BUG-1093) — per-column child lists for the Columns block.
// Device payload: content.columns = [{ children: [...] }, ...] (new SDKs render per column) plus,
// for old SDKs, columnCount and a legacy `children` list interleaved row by row (old SDKs put
// child n in column n mod count; uneven columns can only be approximated there — fail safe).
// Editor: each column is a "column" layout block in content.children, so selection, drop-into
// and every tree helper work unchanged. Migration runs on load and save.

import { decodeEntitiesOnce } from "./inAppMergeTags";
import { TEXT_KEYS } from "./inAppRules";

type B = { id: string; type: string; content: Record<string, any> };

// P3 (DA-BUG-1391): the backend's XSS sanitizer stores author markup HTML-escaped
// (`<i>` → `&lt;i&gt;`). The device + preview decode entities once at render; the editor did not,
// so it showed `&lt;i&gt;`. Decode editable string fields once on load so the editor matches the
// device/preview. Idempotent round-trip: a re-save re-escapes, a re-load re-decodes.
const decStr = (v: unknown) => (typeof v === "string" ? decodeEntitiesOnce(v) : v);
function decodeEditable(c: Record<string, any>): Record<string, any> {
  const out = { ...c };
  TEXT_KEYS.forEach((k) => { if (typeof out[k] === "string") out[k] = decodeEntitiesOnce(out[k]); });
  ["items", "options"].forEach((k) => { if (Array.isArray(out[k])) out[k] = out[k].map(decStr); });
  if (Array.isArray(out.slides)) out.slides = out.slides.map((s: any) => (s && typeof s === "object" ? { ...s, title: decStr(s.title), description: decStr(s.description), cta: decStr(s.cta) } : s));
  return out;
}

const makeColumn = (id: string, children: B[]): B => ({ id, type: "column", content: { children } });

function columnsIn(block: B): B[][] {
  const c = block.content || {};
  const count = Math.max(1, Math.min(4, Number(c.columnCount) || (Array.isArray(c.columns) ? c.columns.length : 2) || 2));
  if (Array.isArray(c.columns) && c.columns.length) {
    const cols = c.columns.map((col: any) => (Array.isArray(col?.children) ? col.children : []));
    while (cols.length < count) cols.push([]);
    return cols;
  }
  const kids: B[] = Array.isArray(c.children) ? c.children : [];
  if (kids.length && kids.every((k) => k?.type === "column")) return kids.map((k) => k.content?.children || []);
  const cols: B[][] = Array.from({ length: count }, () => []);
  kids.forEach((k, i) => cols[i % count].push(k));
  return cols;
}

export function editorBlocksFromApi(blocks: B[], nextId: () => string): B[] {
  return (blocks || []).map((b) => {
    const c = decodeEditable({ ...(b.content || {}) });
    if (b.type === "columns") {
      const cols = columnsIn(b).map((kids) => makeColumn(nextId(), editorBlocksFromApi(kids, nextId)));
      delete c.columns;
      return { ...b, content: { ...c, columnCount: cols.length, children: cols } };
    }
    if (Array.isArray(c.children)) c.children = editorBlocksFromApi(c.children, nextId);
    return { ...b, content: c };
  });
}

export function apiBlocksFromEditor(blocks: B[]): B[] {
  return (blocks || []).map((b) => {
    const c = { ...(b.content || {}) };
    if (b.type === "columns") {
      const kids: B[] = Array.isArray(c.children) ? c.children : [];
      const cols = (kids.length && kids.every((k) => k.type === "column") ? kids.map((k) => k.content?.children || []) : columnsIn(b))
        .map((list: B[]) => apiBlocksFromEditor(list));
      const legacy: B[] = [];
      const rows = Math.max(0, ...cols.map((l: B[]) => l.length));
      for (let r = 0; r < rows; r++) cols.forEach((l: B[]) => { if (l[r]) legacy.push(l[r]); });
      return { ...b, content: { ...c, columnCount: cols.length, columns: cols.map((l: B[]) => ({ children: l })), children: legacy } };
    }
    if (Array.isArray(c.children)) c.children = apiBlocksFromEditor(c.children);
    return { ...b, content: c };
  });
}

// New column count: add empty columns, or move the blocks of removed columns into the last kept one.
export function resizeColumns(cols: B[], count: number, nextId: () => string): B[] {
  const out: B[] = cols.slice(0, count).map((c) => ({ ...c, content: { ...c.content, children: [...(c.content?.children || [])] } }));
  cols.slice(count).forEach((c) => { out[out.length - 1].content.children.push(...(c.content?.children || [])); });
  while (out.length < count) out.push(makeColumn(nextId(), []));
  return out;
}
